import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Upload, Download, Loader2, ShieldCheck, PiggyBank, History, Landmark, Copy, X } from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';

const eur = (v) => Number(v || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
const SEV = {
  ok: { label: 'OK', cls: 'bg-emerald-100 text-emerald-700', row: '' },
  yellow: { label: 'VERIFICA', cls: 'bg-amber-100 text-amber-800', row: 'bg-amber-50' },
  red: { label: 'ERRORE', cls: 'bg-red-100 text-red-700', row: 'bg-red-50' },
};

function ChildrenFix({ row, onFixed }) {
  const [n, setN] = useState('');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (n === '') return toast.error('Inserisci il numero di bimbi <10');
    setSaving(true);
    try {
      const res = await api.post('/payment-checks/set-children', { booking_code: row.booking_code, children_under_10: Number(n), row });
      toast.success(res.data.message);
      if (res.data.row) onFixed(res.data.row);
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore salvataggio'); }
    finally { setSaving(false); }
  };
  return (
    <div className="flex items-center gap-1 mt-1" data-testid={`children-fix-${row.booking_code}`}>
      <span className="text-[10px] text-slate-500">bimbi&lt;10:</span>
      <input type="number" min="0" className="w-12 border rounded px-1 py-0.5 text-xs" value={n} onChange={e => setN(e.target.value)} data-testid={`children-fix-input-${row.booking_code}`} />
      <button onClick={save} disabled={saving} className="text-[10px] bg-blue-600 hover:bg-blue-700 text-white rounded px-1.5 py-0.5 disabled:opacity-50" data-testid={`children-fix-save-${row.booking_code}`}>{saving ? '...' : 'salva'}</button>
    </div>
  );
}

function StatsView({ download }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get('/payment-checks/stats').then(res => setData(res.data))
      .catch(() => toast.error('Errore caricamento storico'));
  }, []);
  if (!data) return <div className="py-10 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div>;
  const t = data.totals;
  return (
    <div className="space-y-4" data-testid="payment-check-stats">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="bg-emerald-50 border-emerald-200"><CardContent className="pt-4 pb-3">
          <p className="text-[11px] font-semibold text-emerald-600 uppercase">💰 Risparmiato (errori evitati)</p>
          <p className="text-2xl font-bold text-emerald-700" data-testid="stats-savings">{eur(t.savings)}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-[11px] font-semibold text-slate-500 uppercase">Differenze già a favore</p>
          <p className="text-2xl font-bold text-slate-700">{eur(t.in_favor)}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-[11px] font-semibold text-slate-500 uppercase">Incassi cliente da verificare</p>
          <p className="text-2xl font-bold text-amber-600">{t.guest_flags}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-[11px] font-semibold text-slate-500 uppercase">Controlli / righe</p>
          <p className="text-2xl font-bold text-slate-700">{data.reports.length} <span className="text-sm font-normal text-slate-400">/ {t.rows}</span></p>
        </CardContent></Card>
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">📄 Elenco PDF controllati</CardTitle></CardHeader>
        <CardContent>
          <table className="w-full text-sm whitespace-nowrap">
            <thead><tr className="text-xs text-slate-500 border-b">
              <th className="text-left py-1.5">Data</th><th className="text-left py-1.5">File</th>
              <th className="text-right py-1.5">Righe</th><th className="text-right py-1.5">Rosse</th>
              <th className="text-right py-1.5">Gialle</th><th className="text-right py-1.5">Importo report</th>
              <th className="text-right py-1.5">💰 Risparmio</th><th className="py-1.5"></th>
            </tr></thead>
            <tbody>
              {data.reports.map(r => (
                <tr key={r.id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`stats-report-${r.id}`}>
                  <td className="py-1.5 text-xs text-slate-500">{new Date(r.created_at).toLocaleString('it-IT')}</td>
                  <td className="py-1.5 text-xs max-w-[200px] truncate">{r.filename}</td>
                  <td className="py-1.5 text-right">{r.summary.total}</td>
                  <td className="py-1.5 text-right text-red-600 font-medium">{r.summary.red}</td>
                  <td className="py-1.5 text-right text-amber-600 font-medium">{r.summary.yellow}</td>
                  <td className="py-1.5 text-right text-xs">{eur(r.summary.total_amount)}</td>
                  <td className="py-1.5 text-right font-bold text-emerald-700">{eur(r.summary.savings)}</td>
                  <td className="py-1.5 text-right">
                    <button onClick={() => download(r.id, r.filename)} className="p-1 rounded hover:bg-slate-100 text-blue-600" title="Scarica PDF corretto">
                      <Download className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
              {data.reports.length === 0 && <tr><td colSpan={8} className="py-6 text-center text-slate-400 text-sm">Nessun controllo ancora eseguito</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">🔁 Anomalie ricorrenti per proprietario</CardTitle></CardHeader>
        <CardContent>
          <table className="w-full text-sm whitespace-nowrap">
            <thead><tr className="text-xs text-slate-500 border-b">
              <th className="text-left py-1.5">Intestazione</th><th className="text-right py-1.5">Anomalie</th>
              <th className="text-right py-1.5">Rosse</th><th className="text-right py-1.5">Ospiti oltre venduto</th>
              <th className="text-right py-1.5">💰 Risparmio</th><th className="text-left py-1.5 pl-4">Prenotazioni</th>
            </tr></thead>
            <tbody>
              {data.recurring.map((r, i) => (
                <tr key={i} className={`border-b last:border-0 ${r.anomalies >= 3 ? 'bg-red-50' : r.anomalies === 2 ? 'bg-amber-50' : ''}`} data-testid={`stats-recurring-${i}`}>
                  <td className="py-1.5 font-medium max-w-[220px] truncate">{r.beneficiary}</td>
                  <td className="py-1.5 text-right font-bold">{r.anomalies}</td>
                  <td className="py-1.5 text-right text-red-600">{r.red || 0}</td>
                  <td className="py-1.5 text-right text-amber-600">{r.guest_flags}</td>
                  <td className="py-1.5 text-right text-emerald-700 font-medium">{eur(r.savings)}</td>
                  <td className="py-1.5 pl-4 text-xs text-slate-400 max-w-[260px] truncate">{(r.codes || []).join(', ')}</td>
                </tr>
              ))}
              {data.recurring.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-slate-400 text-sm">Nessuna anomalia registrata 🎉</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

const downloadB64 = (name, b64, mime) => {
  const bytes = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: mime }));
  const a = document.createElement('a');
  a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
};

function XmlPlanModal({ plan, onClose, onConfirm, generating }) {
  if (!plan) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="xml-plan-modal">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto p-5 space-y-4">
        <div className="flex items-start justify-between">
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2"><Landmark className="h-5 w-5 text-blue-600" /> Genera XML Banca di Asti</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-testid="xml-modal-close"><X className="h-5 w-5" /></button>
        </div>

        {!plan.ready ? (
          <div className="bg-red-50 border border-red-300 rounded-lg p-3 text-sm text-red-800" data-testid="xml-plan-error">
            ❌ {plan.error}
            {(plan.undecided || []).length > 0 && (
              <ul className="mt-2 text-xs list-disc pl-4">
                {plan.undecided.map((u, i) => <li key={i}><b>{u.booking_code}</b> ({u.beneficiary}): {u.reason}</li>)}
              </ul>
            )}
          </div>
        ) : (
          <>
            <div className="flex gap-2 flex-wrap text-xs">
              <span className="px-2.5 py-1 rounded-full bg-blue-100 text-blue-800 font-bold" data-testid="xml-plan-total">{eur(plan.total_amount)}</span>
              <span className="px-2.5 py-1 rounded-full bg-slate-100 font-medium">{plan.transactions_count} bonifici · {plan.beneficiaries_count} beneficiari</span>
              <span className="px-2.5 py-1 rounded-full bg-slate-100 font-medium">{plan.plan.length} {plan.plan.length === 1 ? 'file XML' : 'file XML (split limiti giornalieri)'}</span>
            </div>
            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full text-sm whitespace-nowrap">
                <thead className="bg-slate-50"><tr className="text-xs text-slate-500">
                  <th className="text-left p-2">Lotto</th><th className="text-left p-2">Tipo</th>
                  <th className="text-left p-2">Data esecuzione</th><th className="text-right p-2">Importo</th>
                  <th className="text-right p-2">Bonifici</th><th className="text-left p-2 pl-4">Check-in più vecchio</th>
                </tr></thead>
                <tbody className="divide-y">
                  {plan.plan.map(l => (
                    <tr key={l.batch_index} data-testid={`xml-lot-${l.batch_index}`}>
                      <td className="p-2 font-bold">{l.batch_index}/{plan.plan.length}</td>
                      <td className="p-2">
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${l.transfer_type === 'instant' ? 'bg-violet-100 text-violet-700' : 'bg-slate-100 text-slate-600'}`}>
                          {l.transfer_type === 'instant' ? '⚡ Istantaneo' : 'Ordinario'}
                        </span>
                      </td>
                      <td className="p-2 text-xs">{l.execution_date}</td>
                      <td className="p-2 text-right font-medium">{eur(l.total)}</td>
                      <td className="p-2 text-right">{l.count}</td>
                      <td className="p-2 pl-4 text-xs text-slate-500">{l.oldest_check_in || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {(plan.corrections || []).length > 0 && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs" data-testid="xml-plan-corrections">
            <p className="font-bold text-emerald-800 mb-1">✏️ Correzioni applicate ({plan.corrections.length}) — da riportare poi su HW:</p>
            <ul className="space-y-0.5 text-emerald-900">
              {plan.corrections.map((c, i) => <li key={i}><b>{c.booking_code}</b> · {c.property_code} {c.beneficiary}: {c.action}</li>)}
            </ul>
          </div>
        )}
        {(plan.excluded || []).length > 0 && (
          <div className="bg-slate-50 border rounded-lg p-3 text-xs text-slate-600" data-testid="xml-plan-excluded">
            <p className="font-bold mb-1">🚫 Escluse dal bonifico ({plan.excluded.length}):</p>
            <ul className="space-y-0.5">{plan.excluded.map((c, i) => <li key={i}><b>{c.booking_code}</b> {eur(c.amount)} — {c.reason}</li>)}</ul>
          </div>
        )}
        {(plan.missing_iban || []).length > 0 && (
          <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-xs text-amber-900" data-testid="xml-plan-missing-iban">
            <p className="font-bold mb-1">⚠️ Coordinate non utilizzabili nell’XML SEPA — NON incluse ({plan.missing_iban.length}):</p>
            <ul className="space-y-0.5">{plan.missing_iban.map((c, i) => <li key={i}><b>{c.booking_code}</b> · {c.property_code} {c.beneficiary}: {eur(c.final_amount)} — {c.reason || 'IBAN mancante'}</li>)}</ul>
          </div>
        )}
        {(plan.already_paid || []).length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-800" data-testid="xml-plan-already-paid">
            <p className="font-bold mb-1">🔁 Già presenti in distinte recenti — NON incluse ({plan.already_paid.length}):</p>
            <ul className="space-y-0.5">{plan.already_paid.map((c, i) => <li key={i}><b>{c.reference}</b> {eur(c.amount)} — lotto del {c.previous_batch?.created_at ? new Date(c.previous_batch.created_at).toLocaleDateString('it-IT') : '?'}</li>)}</ul>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" onClick={onClose} data-testid="xml-modal-cancel">Annulla</Button>
          {plan.ready && (
            <Button onClick={onConfirm} disabled={generating} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="xml-modal-confirm">
              {generating ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Landmark className="h-4 w-4 mr-1" />}
              Conferma, scarica e salva in Archivio
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function PaymentCheckTab() {
  const [view, setView] = useState('check'); // 'check' | 'stats'
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [onlyIssues, setOnlyIssues] = useState(true);
  const [decisions, setDecisions] = useState({});
  const [xmlPlan, setXmlPlan] = useState(null);
  const [planLoading, setPlanLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [hwRecap, setHwRecap] = useState(null);

  const loadHistory = useCallback(() => {
    api.get('/payment-checks').then(res => setHistory(res.data.reports || [])).catch(() => {});
  }, []);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const analyze = async () => {
    if (!file) return toast.error('Seleziona il PDF del report pagamenti');
    setLoading(true); setResult(null); setDecisions({}); setHwRecap(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/payment-checks/analyze', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000 });
      setResult(res.data);
      loadHistory();
      const s = res.data.summary;
      toast.success(`Controllo completato: ${s.ok} OK, ${s.yellow} da verificare, ${s.red} errori`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore analisi report');
    } finally { setLoading(false); }
  };

  const [refreshing, setRefreshing] = useState(false);
  const refreshHwData = async () => {
    setRefreshing(true);
    try {
      const res = await api.post('/hw-sync/refresh-deductions');
      toast.success(res.data.detail || 'Aggiornamento avviato');
      setTimeout(() => setRefreshing(false), 180000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore avvio aggiornamento');
      setRefreshing(false);
    }
  };

  const download = async (id, filename) => {
    try {
      const res = await api.get(`/payment-checks/${id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `CONTROLLO_${filename || 'report'}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Errore download'); }
  };

  const openReport = async (id) => {
    try {
      const res = await api.get(`/payment-checks/${id}`);
      setResult(res.data);
      setDecisions(res.data.xml_decisions || {});
      setHwRecap(res.data.xml_corrections?.length ? res.data.xml_corrections : null);
    } catch { toast.error('Errore caricamento'); }
  };

  const setDecision = (idx, action) => setDecisions(d => {
    const nd = { ...d };
    if (nd[idx] === action) delete nd[idx]; else nd[idx] = action;
    return nd;
  });

  const redUndecided = (result?.rows || []).filter((r, i) => r.severity === 'red' && !decisions[i]).length;

  const openXmlPlan = async () => {
    setPlanLoading(true);
    try {
      const res = await api.post(`/payment-checks/${result.id}/xml-plan`, { decisions });
      setXmlPlan(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore calcolo piano XML'); }
    finally { setPlanLoading(false); }
  };

  const generateXml = async () => {
    setGenerating(true);
    try {
      const res = await api.post(`/payment-checks/${result.id}/generate-xml`, { decisions });
      const d = res.data;
      if (d.zip_b64) downloadB64(d.zip_filename, d.zip_b64, 'application/zip');
      else if (d.batches?.[0]) downloadB64(d.batches[0].filename, d.batches[0].xml_b64, 'application/xml');
      setXmlPlan(null);
      setHwRecap(d.corrections?.length ? d.corrections : null);
      toast.success(`✅ ${d.batches.length} XML generat${d.batches.length === 1 ? 'o' : 'i'} (${eur(d.total_amount)}) — distinte salvate in Archivio Pagamenti`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore generazione XML'); }
    finally { setGenerating(false); }
  };

  const copyRecap = () => {
    const text = (hwRecap || []).map(c => `${c.booking_code} · ${c.property_code || ''} ${c.beneficiary || ''} → ${c.action}${c.motivo ? ` (${c.motivo})` : ''}`).join('\n');
    navigator.clipboard.writeText(text).then(() => toast.success('Correzioni copiate negli appunti'));
  };

  const rows = (result?.rows || []).map((r, i) => ({ ...r, _idx: i })).filter(r => !onlyIssues || r.severity !== 'ok');
  const replaceRow = (idx, newRow) => setResult(res => {
    const nrows = res.rows.map((r, i) => (i === idx ? newRow : r));
    return { ...res, rows: nrows, summary: { ...res.summary,
      ok: nrows.filter(r => r.severity === 'ok').length,
      yellow: nrows.filter(r => r.severity === 'yellow').length,
      red: nrows.filter(r => r.severity === 'red').length } };
  });

  return (
    <div className="space-y-4" data-testid="payment-check-tab">
      <div className="flex bg-slate-100 p-1 rounded-lg w-fit">
        <Button variant={view === 'check' ? 'default' : 'ghost'} size="sm" onClick={() => setView('check')} data-testid="pc-view-check">
          <ShieldCheck className="h-4 w-4 mr-1" /> Nuovo controllo
        </Button>
        <Button variant={view === 'stats' ? 'default' : 'ghost'} size="sm" onClick={() => setView('stats')} data-testid="pc-view-stats">
          <PiggyBank className="h-4 w-4 mr-1" /> Storico & Risparmi
        </Button>
      </div>

      {view === 'stats' ? <StatsView download={download} /> : (<>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ShieldCheck className="h-5 w-5 text-blue-600" /> Controllo Pagamenti Proprietari
          </CardTitle>
          <p className="text-xs text-slate-500">
            Carica il PDF "Report" del vecchio sito: verifico ospiti vs codice (regola sottomultipli "di due in due")
            e importi vs prenotazioni registrate. Rosso = paghiamo più del dovuto · Giallo = a nostro favore / da verificare. Tolleranza 1 €.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-3 flex-wrap">
            <input type="file" accept=".pdf" onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="text-sm" data-testid="payment-check-file" />
            <Button onClick={analyze} disabled={loading || !file} data-testid="payment-check-analyze-btn">
              {loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
              Analizza report
            </Button>
            <Button variant="outline" size="sm" onClick={refreshHwData} disabled={refreshing}
              title="Riallinea SUBITO trattenute e stato pagamenti proprietari dal gestionale (di norma avviene ogni notte alle 05:20)"
              data-testid="payment-check-refresh-hw-btn">
              {refreshing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
              {refreshing ? 'Aggiornamento in corso (2-5 min)…' : '🔄 Aggiorna dati da HW'}
            </Button>
          </div>
          {history.length > 0 && !result && (
            <div className="text-xs text-slate-500">
              <p className="font-semibold mb-1">Controlli recenti:</p>
              {history.slice(0, 5).map(h => (
                <button key={h.id} onClick={() => openReport(h.id)} className="block hover:text-blue-600" data-testid={`payment-check-history-${h.id}`}>
                  {new Date(h.created_at).toLocaleString('it-IT')} — {h.filename} · {h.summary.total} righe ({h.summary.red} rosse, {h.summary.yellow} gialle)
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {result && (
        <Card data-testid="payment-check-result">
          <CardContent className="pt-5 space-y-3">
            {(result.warnings || []).length > 0 && (
              <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-xs text-amber-900 space-y-1" data-testid="payment-check-warnings">
                {result.warnings.map((w, i) => <p key={i}>⚠️ <strong>{w}</strong></p>)}
              </div>
            )}
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex flex-wrap gap-2 text-xs">
                <span className="px-2.5 py-1 rounded-full bg-slate-100 font-medium">{result.summary.total} righe · {eur(result.summary.total_amount)}</span>
                <span className="px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 font-medium" data-testid="check-count-ok">OK: {result.summary.ok}</span>
                <span className="px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 font-medium" data-testid="check-count-yellow">Da verificare: {result.summary.yellow}</span>
                <span className="px-2.5 py-1 rounded-full bg-red-100 text-red-700 font-medium" data-testid="check-count-red">Errori: {result.summary.red}</span>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs text-slate-500 flex items-center gap-1.5">
                  <input type="checkbox" checked={onlyIssues} onChange={(e) => setOnlyIssues(e.target.checked)} data-testid="check-only-issues" />
                  Solo anomalie
                </label>
                <Button size="sm" variant="outline" onClick={() => download(result.id, result.filename)} data-testid="payment-check-download-btn">
                  <Download className="h-4 w-4 mr-1" /> Scarica PDF corretto
                </Button>
                <Button size="sm" onClick={openXmlPlan} disabled={planLoading || redUndecided > 0}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  title={redUndecided > 0 ? `Decidi prima le ${redUndecided} righe in errore (Applica/Ignora/Escludi)` : 'Genera le distinte CBI per Banca di Asti e salvale in Archivio Pagamenti'}
                  data-testid="payment-check-generate-xml-btn">
                  {planLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Landmark className="h-4 w-4 mr-1" />}
                  Genera XML Banca di Asti{redUndecided > 0 ? ` (${redUndecided} da decidere)` : ''}
                </Button>
              </div>
            </div>
            <div className="overflow-x-auto max-h-[520px] overflow-y-auto border rounded-lg">
              <table className="w-full text-sm whitespace-nowrap">
                <thead className="bg-slate-50 sticky top-0">
                  <tr className="text-xs text-slate-500">
                    <th className="text-left p-2">Esito</th><th className="text-left p-2">Causale</th>
                    <th className="text-left p-2">Check-in</th><th className="text-right p-2">Importo</th>
                    <th className="text-right p-2">Atteso</th><th className="text-left p-2">Intestazione</th>
                    <th className="text-left p-2">Ospiti</th><th className="text-left p-2">Motivo</th>
                    <th className="text-left p-2">Azione bonifico</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {rows.map((r) => {
                    const d = decisions[r._idx];
                    const cancelled = (r.reasons || []).some(x => x.includes('CANCELLATA'));
                    const canApply = cancelled || (r.expected_amount !== null && r.expected_amount !== undefined);
                    const btn = (act, label, activeCls) => (
                      <button onClick={() => setDecision(r._idx, act)}
                        className={`px-1.5 py-0.5 rounded text-[10px] font-semibold border transition-colors ${d === act ? activeCls : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-100'}`}
                        data-testid={`decision-${act}-${r.booking_code}`}>
                        {label}
                      </button>
                    );
                    return (
                    <tr key={r._idx} className={SEV[r.severity].row} data-testid={`check-row-${r.booking_code}`}>
                      <td className="p-2"><span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${SEV[r.severity].cls}`}>{SEV[r.severity].label}</span></td>
                      <td className="p-2 text-xs">
                        <span className="font-mono">{r.booking_code}</span>
                        {(r.property_name || r.property_code) && <p className="text-[10px] text-slate-500 max-w-[220px] truncate" title={r.property_name} data-testid={`row-property-${r.booking_code}`}>{r.property_code}{r.property_name ? ` · ${r.property_name}` : ''}</p>}
                      </td>
                      <td className="p-2 text-xs">{r.check_in}</td>
                      <td className="p-2 text-right font-medium">{eur(r.amount)}</td>
                      <td className="p-2 text-right text-xs">{r.expected_amount !== null && r.expected_amount !== undefined ? eur(r.expected_amount) : '—'}</td>
                      <td className="p-2 text-xs max-w-[180px] truncate">{r.beneficiary}</td>
                      <td className="p-2 text-xs">
                        {r.sold_persons ? `venduta S${r.sold_persons}` : '—'}{r.guests_count ? ` · osp. ${r.guests_count}` : ''}{r.max_guests ? ` / max ${r.max_guests}` : ''}
                        {r.children_under_10 !== undefined && r.children_under_10 !== null && <span className="text-emerald-600"> · 👶{r.children_under_10}&lt;10</span>}
                        {(r.children_age_check || (r.children_no_age > 0 && r.severity !== 'ok')) && <ChildrenFix row={r} onFixed={(nr) => replaceRow(r._idx, nr)} />}
                      </td>
                      <td className="p-2 text-xs text-slate-600 whitespace-normal max-w-[380px]">{(r.reasons || []).join(' · ') || '—'}</td>
                      <td className="p-2">
                        {r.severity !== 'ok' ? (
                          <div className="space-y-1">
                            <div className="flex gap-1">
                              {canApply && btn('apply', cancelled ? '✏️ Applica (escludi)' : `✏️ Applica ${eur(r.expected_amount)}`, 'bg-emerald-600 border-emerald-600 text-white')}
                              {btn('ignore', 'Ignora', 'bg-slate-600 border-slate-600 text-white')}
                              {btn('exclude', 'Escludi', 'bg-red-600 border-red-600 text-white')}
                            </div>
                            <p className="text-[10px] text-slate-400" data-testid={`decision-effect-${r.booking_code}`}>
                              {d === 'apply' ? (cancelled ? '→ esclusa dal bonifico' : (
                                <span className="text-emerald-700 font-semibold">
                                  → bonifico {eur(r.expected_amount)}
                                  {r.amount - r.expected_amount > 0.005 ? ` (trattenuti ${eur(r.amount - r.expected_amount)})`
                                    : r.expected_amount - r.amount > 0.005 ? ` (integrazione +${eur(r.expected_amount - r.amount)})` : ''}
                                </span>))
                                : d === 'ignore' ? `→ pagherò ${eur(r.amount)} (come da report)`
                                : d === 'exclude' ? '→ esclusa dal bonifico'
                                : r.severity === 'red' ? '⚠️ da decidere' : `→ senza scelta pagherò ${eur(r.amount)} (come da report)`}
                            </p>
                          </div>
                        ) : <span className="text-[10px] text-emerald-600">→ {eur(r.amount)}</span>}
                      </td>
                    </tr>
                    );
                  })}
                  {rows.length === 0 && <tr><td colSpan={9} className="p-6 text-center text-slate-400 text-sm">Nessuna anomalia 🎉 (deseleziona "Solo anomalie" per vedere tutte le righe)</td></tr>}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {hwRecap && (
        <Card className="border-emerald-300" data-testid="hw-corrections-recap">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2">✏️ Correzioni da riportare su HW — {hwRecap.length}</CardTitle>
              <Button size="sm" variant="outline" onClick={copyRecap} data-testid="copy-hw-recap-btn">
                <Copy className="h-4 w-4 mr-1" /> Copia elenco
              </Button>
            </div>
            <p className="text-xs text-slate-500">L'XML per la banca è già corretto: queste modifiche vanno fatte SOLO sul vecchio sito, con calma, per riallineare i dati.</p>
          </CardHeader>
          <CardContent>
            <ul className="text-xs space-y-1">
              {hwRecap.map((c, i) => (
                <li key={i} className="border-b last:border-0 pb-1">
                  <b className="font-mono">{c.booking_code}</b> · {c.property_code} {c.beneficiary} — <span className="text-emerald-700 font-medium">{c.action}</span>
                  {c.motivo && <span className="text-slate-400"> ({c.motivo})</span>}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {result?.new_properties?.length > 0 && (
        <Card className="border-blue-200" data-testid="new-properties-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              🆕 Case nuove da verificare (promozioni) — {result.new_properties.length}
            </CardTitle>
            <p className="text-xs text-slate-500">
              Case che appaiono per la PRIMA volta in un file di pagamenti e non hanno promozioni inserite (né attive né cessate):
              verifica a mano se hanno promozioni da caricare. Dopo questo file non verranno più segnalate.
            </p>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full text-sm whitespace-nowrap">
                <thead className="bg-blue-50">
                  <tr className="text-xs text-slate-500">
                    <th className="text-left p-2">Codice casa</th>
                    <th className="text-left p-2">Nome</th>
                    <th className="text-left p-2">Intestazione</th>
                    <th className="text-left p-2">Prenotazioni nel file</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {result.new_properties.map((p) => (
                    <tr key={p.property_code} className="bg-blue-50/40" data-testid={`new-property-row-${p.property_code}`}>
                      <td className="p-2 font-mono text-xs font-bold">{p.property_code}</td>
                      <td className="p-2 text-xs max-w-[220px] truncate">{p.property_name || '—'}</td>
                      <td className="p-2 text-xs max-w-[200px] truncate">{p.beneficiary || '—'}</td>
                      <td className="p-2 text-xs text-slate-500 whitespace-normal max-w-[380px]">{(p.bookings || []).join(', ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
      </>)}

      <XmlPlanModal plan={xmlPlan} onClose={() => setXmlPlan(null)} onConfirm={generateXml} generating={generating} />
    </div>
  );
}
